/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: AgentFormView.tsx (Agents)
 🗓️ Created: 2025-01-15
 🧩 Description:
    Form view for creating and editing Agents.
    Manages agent configuration with general and behavior settings.

 🔧 Notes:
    - Refactored from monolithic file
    - Logic partially delegated to hooks/
    - API calls moved to api/
    - Complex settings logic kept in component

 🔁 Last Updated: 2025-01-15 by Rodrigo Cuadra (Refactored)
────────────────────────────────────────────────────────────────*/

import { ChangeEvent, useCallback, useState, useMemo, useRef } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { User, Search, Copy, Plus } from 'lucide-react';
import { useI18n } from '../../hooks/useI18n';
import { ModuleFormHeader } from '../../components/layouts';
import { FixedActionBar } from '../../components/ui/FixedActionBar';
import { useSaveWithAction } from '../../hooks/useSaveWithAction';
import { QuickSearchModal } from '../../components/ui/QuickSearchModal';
import { AgentGeneralSection } from './AgentGeneralSection';
import { AgentBehaviorSection } from './AgentBehaviorSection';
import { AgentStatus, AgentType, AgentStateMode } from './types/agents.types';
import { STATUS_OPTIONS, AGENT_TYPE_OPTIONS, STATE_MODE_OPTIONS } from './constants';
import { useAgentData } from './hooks/useAgentData';
import { useAgentForm } from './hooks/useAgentForm';
import { fetchQuickListItems } from './api/agentsApi';
import { navigateToAgentDuplicate } from './agentsActions';

export default function AgentFormView() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const { id } = useParams<{ id?: string }>();
  const [searchParams] = useSearchParams();
  const duplicateId = searchParams.get('duplicate');
  const isEditMode = Boolean(id && id !== 'new');

  const { mappedData, isLoading } = useAgentData(id, duplicateId);

  const {
    formState,
    extraSettings,
    errors,
    isSaving,
    hasChanges,
    handleFieldChange,
    handleExtraSettingsChange,
    handleSave,
    handleCancel
  } = useAgentForm(id, duplicateId, mappedData);

  const [showQuickList, setShowQuickList] = useState(false);
  const [quickListItems, setQuickListItems] = useState<Array<{ id: number; name: string; extension?: string | null }>>([]);
  const [quickListSearch, setQuickListSearch] = useState('');
  const [isQuickListLoading, setIsQuickListLoading] = useState(false);
  const quickListLoadingRef = useRef(false);

  const handleStatusChange = useCallback((event: ChangeEvent<HTMLSelectElement>) => {
    const { value } = event.target;
    if (STATUS_OPTIONS.includes(value as AgentStatus)) {
      handleFieldChange('initialStatus', value as AgentStatus);
    }
  }, [handleFieldChange]);

  const handleStateModeChange = useCallback((event: ChangeEvent<HTMLSelectElement>) => {
    const { value } = event.target;
    if (STATE_MODE_OPTIONS.includes(value as AgentStateMode)) {
      handleFieldChange('stateMode', value as AgentStateMode);
    }
  }, [handleFieldChange]);

  const handleAgentTypeChange = useCallback((event: ChangeEvent<HTMLSelectElement>) => {
    const { value } = event.target;
    if (AGENT_TYPE_OPTIONS.includes(value as AgentType)) {
      handleFieldChange('agentType', value as AgentType);
    }
  }, [handleFieldChange]);

  const handleDuplicateFromForm = useCallback(() => {
    if (!isEditMode || !id) return;
    navigateToAgentDuplicate(navigate, id);
  }, [id, isEditMode, navigate]);

  // Navigate to new
  const handleAddNew = useCallback(() => {
    navigate('/pbx/call-center/agents/new');
  }, [navigate]);

  const loadQuickList = useCallback(async () => {
    if (quickListLoadingRef.current) {
      return;
    }

    quickListLoadingRef.current = true;
    setIsQuickListLoading(true);
    try {
      const items = await fetchQuickListItems(100);
      setQuickListItems(items);
    } catch {
      // Error handling is done in the hook
      setQuickListItems([]);
    } finally {
      setIsQuickListLoading(false);
      quickListLoadingRef.current = false;
    }
  }, []);

  const filteredQuickList = useMemo(() => {
    const query = quickListSearch.trim().toLowerCase();
    if (!query) {
      return quickListItems;
    }

    return quickListItems.filter((item) =>
      item.name.toLowerCase().includes(query) ||
      (item.extension ?? '').toLowerCase().includes(query)
    );
  }, [quickListItems, quickListSearch]);

  // Enhanced save handler for useSaveWithAction
  const handleSaveInternal = useCallback(async () => {
    const savedData = await handleSave();

    // If no changes were detected, return current data to allow navigation
    if (!savedData && isEditMode && id) {
      // Return current data with ID/UUID for navigation purposes
      return { id, uuid: id };
    }

    return savedData;
  }, [handleSave, isEditMode, id]);

  // Use reusable hook for save actions
  const handleSaveWithAction = useSaveWithAction({
    onSave: handleSaveInternal,
    listPath: '/pbx/call-center/agents',
    newPath: '/pbx/call-center/agents/new',
    mode: isEditMode ? 'edit' : 'create',
    getEditPath: (savedData) => {
      // Agents use ID for navigation
      return `/pbx/call-center/agents/${savedData.uuid || savedData.id}`;
    },
    getFormData: () => ({
      formState,
      extraSettings
    }),
    storageKey: 'agents:saveAndNewData'
  });

  return (
    <div className="p-6 pb-12 space-y-4">
      <ModuleFormHeader
        icon={<User className="h-5 w-5 text-primary" />}
        title={isEditMode ? `${t('agents.form.editTitle')} - ${formState.extension || ''}` : t('agents.form.createTitle')}
        backButtonText={t('common.list')}
        onBack={() => navigate(-1)}
        actions={[
          // 1. Quick Search (always visible)
          {
            icon: <Search className="h-5 w-5 text-muted-foreground" />,
            tooltip: t('agents.actions.quickList'),
            onClick: () => {
              setShowQuickList(true);
              void loadQuickList();
            },
          },
          // 2. Duplicate (edit mode only)
          ...(isEditMode
            ? [{
              icon: <Copy className="h-5 w-5 text-muted-foreground" />,
              tooltip: t('agents.actions.duplicate'),
              onClick: handleDuplicateFromForm,
            }]
            : []),
          // 3. Add New (always visible)
          {
            icon: <Plus className="h-5 w-5 text-muted-foreground" />,
            tooltip: t('agents.actions.add'),
            onClick: handleAddNew,
          }
        ]}
      />

      <div className="space-y-4 pb-10">
        <div className="space-y-4">
          <AgentGeneralSection
            formData={formState}
            onFieldChange={handleFieldChange}
            onStatusChange={handleStatusChange}
            onStateModeChange={handleStateModeChange}
            onAgentTypeChange={handleAgentTypeChange}
          />

          <AgentBehaviorSection
            formData={formState}
            onFieldChange={handleFieldChange}
          />
        </div>
      </div>

      <FixedActionBar
        onSaveWithAction={handleSaveWithAction}
        onCancel={handleCancel}
        isSaving={isSaving}
        isLoading={isLoading}
        isEditMode={isEditMode}
        saveText={t('common.save')}
        cancelText={t('common.cancel')}
        useSaveDropdown={true}
        saveDropdownStorageKey="agents:saveActionPreference"
      />

      <QuickSearchModal
        isOpen={showQuickList}
        onClose={() => {
          setShowQuickList(false);
          setQuickListSearch('');
          setIsQuickListLoading(false);
          quickListLoadingRef.current = false;
        }}
        title={t('agents.quickList.title', 'Agents')}
        items={filteredQuickList}
        isLoadingInitial={isQuickListLoading}
        isSearching={false}
        searchValue={quickListSearch}
        onSearchChange={setQuickListSearch}
        onItemSelect={(item) => {
          setShowQuickList(false);
          setQuickListSearch('');
          navigate(`/pbx/call-center/agents/${(item as any).uuid || item.id}`, { replace: true });
        }}
        currentItemId={id}
        renderItem={(item, isCurrent) => (
          <button
            type="button"
            disabled={isCurrent}
            onClick={() => {
              if (isCurrent) return;
              setShowQuickList(false);
              setQuickListSearch('');
              navigate(`/pbx/call-center/agents/${(item as any).uuid || item.id}`, { replace: true });
            }}
            className={`w-full text-left p-3 rounded-md border border-border transition-colors ${isCurrent
              ? 'bg-primary/20 border-2 border-primary cursor-default opacity-60'
              : 'hover:bg-muted/40'
              }`}
          >
            <div className="font-medium text-foreground">
              {item.name}
            </div>
            {item.extension && (
              <div className="text-xs text-muted-foreground mt-0.5">
                {item.extension}
              </div>
            )}
          </button>
        )}
        searchPlaceholder={t('agents.quickList.searchPlaceholder', 'Search by name or extension')}
        emptyMessage={t('agents.quickList.empty', 'No agents found')}
        headerIcon={<User className="h-5 w-5" />}
        minSearchChars={0}
      />
    </div>
  );
}
